import { MoneyType } from "~/components/Money";
import { type CurrencyCode } from "~/graphql/mainTypes";
import { ClientArenaType, EntityType } from "./types";

/**
 * Converts a numeric value to a MoneyType object
 * @param value Numeric value to convert
 * @param currency Currency code (defaults to USD)
 * @returns MoneyType object or null if value is null/undefined
 */
export const toMoneyType = (
  value: number | null | undefined,
  currency: string,
): MoneyType | null => {
  if (value === null || value === undefined) {
    return null;
  }

  // Determine precision dynamically based on decimal places
  const valueStr = value.toString();
  const decimalParts = valueStr.split(".");
  const precision = decimalParts.length > 1 ? decimalParts[1].length : 0;

  // Convert to integer (avoid floating point precision issues)
  const integerAmount = Number(valueStr.replace(".", ""));

  return {
    amount: integerAmount,
    currency: currency as CurrencyCode,
    precision: precision,
  };
};

/**
 * Creates MoneyType objects for client data financial values
 */
export const getClientMoneyValues = (clientArena: ClientArenaType) => ({
  baseEumUsd: toMoneyType(clientArena.baseEumUsd, "USD"),
  currentBlendSoftwareEumLocal: toMoneyType(clientArena.currentBlendSoftwareEumLocal, "USD"),
  currentBlendSoftwareEumUsd: toMoneyType(clientArena.currentBlendSoftwareEumUsd, "USD"),
});

/**
 * Creates MoneyType objects for entity financial values
 */
export const getEntityMoneyValues = (entity: EntityType) => ({
  commitmentsLocal: toMoneyType(entity.commitmentsLocal, entity.currency || "USD"),
  commitmentsUsd: toMoneyType(entity.commitmentsUsd, "USD"),
  contributionsLocal: toMoneyType(entity.contributionsLocal, entity.currency || "USD"),
  contributionsUsd: toMoneyType(entity.contributionsUsd, "USD"),
  redemptionsLocal: toMoneyType(entity.redemptionsLocal, entity.currency || "USD"),
  redemptionsUsd: toMoneyType(entity.redemptionsUsd, "USD"),
  distributionsLocal: toMoneyType(entity.distributionsLocal, entity.currency || "USD"),
  distributionsUsd: toMoneyType(entity.distributionsUsd, "USD"),
  currentBlendSoftwareEumLocal: toMoneyType(
    entity.currentBlendSoftwareEumLocal,
    entity.currency || "USD",
  ),
  currentBlendSoftwareEumUsd: toMoneyType(entity.currentBlendSoftwareEumUsd, "USD"),
});
